import { useTranslation } from "react-i18next";
import {
    TEXT_FIELD_TYPE,
    DATE_RANGE_FIELD_TYPE,
    AUTOCOMPLETE_FIELD_TYPE,
    NUMERIC_RANGE_FIELD_TYPE,
    BOOLEAN_FIELD_TYPE,
} from "../../helpers/constants";
import { IconButton } from "@mui/material";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import { useProposalFilteringData } from "../../services/productsService";

import { formatCurrency } from "../../components/common/PriceDisplay";
import DateService from "../../services/dateService";
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import FlakyIcon from '@mui/icons-material/Flaky';
import { convertFlatListFromBackendToTranslatedOptionObject } from "../../helpers/methods";
import { ProposalAcceptDialog } from "./ProposalAcceptDialog";

export const PROPOSAL_TABLE_NAME = "proposal_table";

export const useProposalTableFieldsConfig = () => {
    const { t } = useTranslation();

    const { data: filteringData } = useProposalFilteringData()

    const { openDialog: openAcceptProposalDialog } = useDialogBetter();
    
    const handleOpenProposalDialog = (e, proposalId, proposalType) => {
        e.stopPropagation();
        e.preventDefault();
        openAcceptProposalDialog({
            Component: ProposalAcceptDialog,
            componentProps: {
                proposalId: proposalId,
                proposalType: proposalType,
            },
        });
    };

    return [
        {
            name: "proposal_type",
            getValueCallback: (rowData) => t(`products.columns.${rowData.proposal_type}`) ,
            label: t("products.columns.proposal_type"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            fieldConfig: {
                options: convertFlatListFromBackendToTranslatedOptionObject(
                    filteringData?.proposal_type,
                    "id",
                    "name",
                    "products.columns",
                ),
                isObjectOption: true,
                disableClearable: true
            },
        },
        {
            name: "creator",
            getValueCallback: (rowData) => rowData.creator,
            label: t("products.columns.creator"),
            filterType: TEXT_FIELD_TYPE,
        },
        {
            name: "created",
            getValueCallback: (rowData) =>
                DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.created),
            label: t("products.columns.proposal_created"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "investment",
            getValueCallback: (rowData) => rowData.investment,
            label: t("products.columns.investment"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            fieldConfig: {
                optionLabelKey: "investment_name_sales",
                options: filteringData?.investment,
                multiple: false,
                isObjectOption: true,
                disableClearable: true,
            },
        },
        {
            name: "product_number",
            getValueCallback: (rowData) => rowData.product_number,
            label: t("products.columns.product_number"),
            filterType: TEXT_FIELD_TYPE,
        },
        {
            name: "product_type",
            getValueCallback: (rowData) => rowData.product_type,
            label: t("products.columns.product_type"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            fieldConfig: {
                optionLabelKey: "prodtype_name",
                options: filteringData?.product_type,
                multiple: true,
                isObjectOption: true,
                disableClearable: true,
            },
        },
        {
            name: "product_price_net",
            getValueCallback: (rowData) => formatCurrency(rowData.product_price_net),
            label: t("products.columns.product_price_net"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
        },
        {
            name: "product_price_gross",
            getValueCallback: (rowData) => formatCurrency(rowData.product_price_gross),
            label: t("products.columns.product_price_gross"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
        },
        {
            name: "product_discount",
            getValueCallback: (rowData) => rowData.product_discount?.toFixed(2) ,
            label: t("products.columns.product_discount"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
        },
        {
            name: "proposal_discount",
            getValueCallback: (rowData) => rowData.proposal_discount?.toFixed(2),
            label: t("products.columns.proposal_discount"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
        },
        {
            name: "product_status",
            getValueCallback: (rowData) => rowData.product_status,
            label: t("products.columns.product_status"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            fieldConfig: {
                optionLabelKey: "status_name",
                options: filteringData?.product_status,
                multiple: true,
                isObjectOption: true,
                disableClearable: true,
            },
        },
        {
            name: "proposal_status",
            getValueCallback: (rowData) => rowData.proposal_status,
            label: t("products.columns.proposal_status"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            fieldConfig: {
                optionLabelKey: "status_name",
                options: filteringData?.product_status,
                multiple: true,
                isObjectOption: true,
                disableClearable: true
            },
        },
        {
            name: "proposal_note",
            getValueCallback: (rowData) => rowData.proposal_note,
            label: t("products.columns.proposal_note"),
            filterType: TEXT_FIELD_TYPE,
        },
        {
            name: "proposal_accept_user",
            getValueCallback: (rowData) => rowData.proposal_accept_user,
            label: t("products.columns.proposal_accept_user"),
            filterType: TEXT_FIELD_TYPE,
        },
        {
            name: "proposal_accept_date",
            getValueCallback: (rowData) =>
                DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.proposal_accept_date),
            label: t("products.columns.proposal_accept_date"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "proposal_accept_note",
            getValueCallback: (rowData) => rowData.proposal_accept_note,
            label: t("products.columns.proposal_accept_note"),
            filterType: TEXT_FIELD_TYPE,
        },
        {
            name: "proposal_accepted",
            label: t("products.columns.proposal_accepted"),
            filterType: BOOLEAN_FIELD_TYPE,
            getValueCallback: (rowData) => (
              rowData.proposal_accepted === true ? <CheckIcon color={"success"}/>
            : rowData.proposal_accepted === false ? <ClearIcon color={"error"}/>
            : <IconButton
                    color={"primary"}
                    onClick={(e) => handleOpenProposalDialog(e, rowData.id, rowData.proposal_type)}
                >
                    <FlakyIcon />
               </IconButton>
            ),
            allowChangeVisibility: false,
            excludedFromExport: true,
        },
    ];
};
